import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { VoiceCommander } from "./VoiceCommander";
import { MobileIIFFAB } from "./MobileIIFFAB";
import { SkipLink } from "./a11y/SkipLink";
import { LangSwitcher } from "./LangSwitcher";
import { ThemeSwitcher } from "../theme/ThemeSwitcher";
import { OfflineBanner, SyncQueueChip } from "./sync/OfflineBanner";
import { CommandPalette } from "./CommandPalette";
import { FieldBottomNav } from "./FieldBottomNav";

// ---------------------------------------------------------------------------
//  Static nav structure. Section *titles* are translation keys
//  (`common:nav.sections.*`); item labels remain literal for now so existing
//  routes keep working. As pages migrate to react-i18next, replace label
//  strings with `t('nav.items.<key>')`.
// ---------------------------------------------------------------------------

const sections = [
  { title: "Operations", items: [
    { to: "/dashboard",     label: "Dashboard" },
    { to: "/sos",           label: "🚨 SOS Alerts" },
    { to: "/flow",          label: "System Flow" },
    { to: "/visitor",       label: "🪪 Visitor Desk · भेटकर्ता" },
    { to: "/station-diary", label: "Station Diary (GD)" },
    { to: "/complaints",    label: "Complaints" },
    { to: "/complaints/voice", label: "🎙 Voice Complaint · आवाज तक्रार" },
    { to: "/firs",          label: "FIRs" },
    { to: "/my-cases",      label: "📁 My Cases" },
    { to: "/case-diary",    label: "📔 Case Diary · तपास डायरी (§192)" },
    { to: "/transfers/incoming", label: "Incoming Transfers" },
    { to: "/aio-sahayak",   label: "🤖 AIO Sahayak · आयो सहायक" },
    { to: "/panchanamas",   label: "Panchanamas" },
    { to: "/arrests",       label: "Arrests" },
    { to: "/charge-sheets", label: "Charge Sheets" },
  ]},
  { title: "Investigation", items: [
    { to: "/medico-legal",  label: "MLC" },
    { to: "/forensic",      label: "FSL" },
    { to: "/malkhana",      label: "Malkhana" },
    { to: "/court/filings", label: "Court" },
    { to: "/process-docs",  label: "Summons / Warrants" },
    { to: "/bail",          label: "Bail" },
    { to: "/identity/aadhaar", label: "Aadhaar" },
  ]},
  { title: "Registers", items: [
    { to: "/missing-persons", label: "Missing Persons" },
    { to: "/lost-found",      label: "Lost & Found" },
    { to: "/wanted",          label: "Wanted / Absconders" },
    { to: "/verification",    label: "Verification" },
    { to: "/ncr",             label: "NCR (Non-cognisable)" },
  ]},
  { title: "Oversight", items: [
    { to: "/supervisor",    label: "Supervisor" },
    { to: "/reports",       label: "Reports" },
    { to: "/audit-trail",   label: "Audit" },
    { to: "/alerts",        label: "Alerts" },
    { to: "/search",        label: "Search" },
    { to: "/map",           label: "Map" },
  ]},
  { title: "Knowledge", items: [
    { to: "/mahallm",           label: "🇮🇳 About MahaLLM™" },
    { to: "/tools/captures",    label: "🔧 Tool Captures" },
    { to: "/integrations",      label: "🔌 Integrations Hub" },
    { to: "/templates",         label: "Templates" },
    { to: "/workflows",         label: "Workflows" },
    { to: "/crime-workflows",   label: "Crime UCs" },
    { to: "/sops",              label: "SOPs" },
    { to: "/panchanama-types",  label: "Panchanama Types" },
    { to: "/ai-modules",        label: "AI Modules" },
    { to: "/ai-per-step",       label: "AI per Step" },
    { to: "/mo-patterns",       label: "MO Patterns" },
    { to: "/advanced-tools",    label: "Advanced Tools" },
    { to: "/field-kits",        label: "Field Kits" },
    { to: "/hardware",          label: "Hardware" },
    { to: "/registers",         label: "Registers" },
    { to: "/migration-map",     label: "Current → Dawell" },
    { to: "/court-compliance",  label: "Court Compliance" },
    { to: "/document-linking",  label: "Document Linking" },
    { to: "/ux-innovations",    label: "UX Innovations" },
    { to: "/future-features",   label: "Future Roadmap" },
  ]},
  { title: "Admin", items: [
    { to: "/admin/officers",     label: "Officers" },
    { to: "/admin/ps",           label: "PS" },
    { to: "/admin/devices",      label: "Devices" },
    { to: "/admin/integrations", label: "Integrations" },
    { to: "/admin/ai-config",    label: "AI Config" },
    { to: "/admin/ai-config/aio-sahayak", label: "AIO Sahayak Config" },
    { to: "/admin/audit-logs",   label: "Audit Logs" },
    { to: "/admin/configs",      label: "Configs" },
    { to: "/admin/roles",        label: "Roles" },
    { to: "/admin/iif",          label: "📄 CCTNS IIF · IIF प्रशासन" },
    { to: "/admin/pcr-jobs",     label: "🔍 PCR pull jobs (CCTNS)" },
    { to: "/iif/drift",          label: "⚠ IIF drift dashboard" },
    { to: "/cctns/iif",          label: "IIF Hub · CCTNS फॉर्म" },
    { to: "/admin/citizen-consents", label: "🇮🇳 DigiLocker consents · डिजिलॉकर संमती" },
  ]},
] as const;

const STORAGE_KEY = "mhpol.nav.open";

function activeSectionTitle(pathname: string): string {
  for (const sec of sections) {
    if (sec.items.some((it) => pathname === it.to || pathname.startsWith(it.to + "/"))) {
      return sec.title;
    }
  }
  return sections[0].title;
}

export function Layout() {
  const location = useLocation();
  const { t } = useTranslation("common");

  const [open, setOpen] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null) return stored === "1";
    return window.innerWidth >= 1024;
  });
  useEffect(() => { localStorage.setItem(STORAGE_KEY, open ? "1" : "0"); }, [open]);

  // Auto-close on route change (mobile only)
  useEffect(() => { if (window.innerWidth < 1024) setOpen(false); }, [location.pathname]);

  // Close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const [online, setOnline] = useState<boolean>(() => navigator.onLine);
  useEffect(() => {
    const up = () => setOnline(true), down = () => setOnline(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
    };
  }, []);

  const currentSection = activeSectionTitle(location.pathname);
  const currentSectionLabel = t(`nav.sections.${currentSection}`, { defaultValue: currentSection });

  return (
    <div className="min-h-screen bg-[var(--c-surface-muted)]">
      {/* Visible-on-focus skip link */}
      <SkipLink label={t("nav.skip")} />

      {/* Persistent offline banner */}
      <OfflineBanner />

      {/* MahaLLM sovereignty strip */}
      <NavLink to="/mahallm" aria-label="About MahaLLM"
        className="block bg-gradient-to-r from-orange-500 via-white to-emerald-600 text-mhpol-900 hover:brightness-105">
        <div className="mx-auto flex max-w-7xl items-center justify-center gap-2 px-3 py-1 text-[11px] sm:text-xs font-medium">
          <span aria-hidden="true">🇮🇳</span>
          <strong>MahaLLM™</strong>
          <span className="hidden sm:inline">— Indigenous · Sovereign · Customised · 7 Patents Filed (पेटंट दाखल)</span>
          <span className="sm:hidden">— स्वदेशी · सार्वभौम</span>
        </div>
      </NavLink>

      {/* Top bar */}
      <header className="sticky top-0 z-nav bg-mhpol-900 text-white shadow">
        <div className="mx-auto flex max-w-7xl items-center gap-3 px-3 py-2 sm:px-4 sm:py-3">
          <button
            type="button"
            aria-label={open ? t("nav.close") : t("nav.menu")}
            aria-expanded={open}
            aria-controls="primary-nav"
            onClick={() => setOpen((v) => !v)}
            className="inline-flex shrink-0 items-center gap-2 rounded border border-white/20 bg-mhpol-700 px-3 py-1.5 text-sm hover:bg-mhpol-600 focus-ring min-h-tap"
          >
            <span aria-hidden="true" className="text-base leading-none">{open ? "✕" : "☰"}</span>
            <span className="hidden sm:inline">{open ? t("nav.close") : currentSectionLabel}</span>
          </button>

          <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
            <img src="/mh-police-logo.svg" alt={t("app.name")} className="h-9 w-9 shrink-0 sm:h-12 sm:w-12" />
            <div className="min-w-0">
              <div className="truncate text-sm font-semibold sm:text-lg">{t("app.name")}</div>
              <div className="hidden truncate text-xs text-mhpol-50/80 sm:block">
                v1.0 · Shivajinagar PS (MH-PS-411) · <span lang="sa">{t("app.tagline")}</span>
              </div>
            </div>
          </div>

          <VoiceCommander />

          <SyncQueueChip />

          <span
            className={`shrink-0 rounded px-2 py-0.5 text-[10px] font-semibold ${
              online ? "bg-emerald-500 text-white" : "bg-amber-500 text-mhpol-900"
            }`}
            role="status"
            aria-live="polite"
            title={online ? t("status.online") : t("status.offline")}
          >
            {online ? `● ${t("status.online")}` : `⚠ ${t("status.offline")}`}
          </span>

          <ThemeSwitcher />
          <LangSwitcher />

          <div className="hidden text-right text-xs sm:block">
            <NavLink to="/me" className="hover:text-white">Inspector A. Patil · IO</NavLink>
            <div className="text-mhpol-50/70">
              <NavLink to="/me" className="hover:text-white">{t("nav.profile")}</NavLink> ·{" "}
              <a href="/help" className="hover:text-white">{t("nav.help")}</a> ·{" "}
              <a href="/settings" className="hover:text-white">{t("nav.settings")}</a>
            </div>
          </div>
        </div>
        <div className="border-t border-mhpol-700 bg-[#102f6e] px-3 py-1 text-xs text-mhpol-50/80 sm:hidden">
          <span className="opacity-60">Section:</span> <span className="font-semibold">{currentSectionLabel}</span>
        </div>
      </header>

      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-nav bg-black/40 backdrop-blur-sm transition-opacity"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Left drawer */}
      <aside
        id="primary-nav"
        className={`fixed left-0 top-0 z-drawer flex h-full w-[85vw] max-w-sm flex-col border-r border-default bg-surface shadow-modal transition-transform duration-200 ease-out ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        aria-hidden={!open}
        role="navigation"
        aria-label={t("nav.navigation")}
      >
        <div className="flex items-center justify-between border-b border-default bg-mhpol-900 px-4 py-3 text-white">
          <div className="text-sm font-semibold">{t("nav.navigation")}</div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label={t("nav.close")}
            className="rounded p-1 hover:bg-white/10 focus-ring tap"
          >
            ✕
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-2 py-3">
          {sections.map((sec) => {
            const isCurrent = sec.title === currentSection;
            const sectionLabel = t(`nav.sections.${sec.title}`, { defaultValue: sec.title });
            return (
              <details key={sec.title} open={isCurrent} className="group mb-1 rounded border border-transparent open:border-default open:bg-[var(--c-surface-muted)]">
                <summary className="flex cursor-pointer list-none items-center justify-between rounded px-3 py-2 text-xs font-semibold uppercase tracking-wider text-default hover:bg-[var(--c-surface-muted)]">
                  <span>{sectionLabel}</span>
                  <span aria-hidden="true" className="text-subtle transition-transform group-open:rotate-90">›</span>
                </summary>
                <ul className="mt-1 space-y-0.5 px-1 pb-2">
                  {sec.items.map((it) => (
                    <li key={it.to}>
                      <NavLink
                        to={it.to}
                        end
                        className={({ isActive }) =>
                          `block rounded px-3 py-2 text-sm transition-colors min-h-tap ${
                            isActive
                              ? "bg-mhpol-900 font-semibold text-white"
                              : "text-default hover:bg-[var(--c-brand-soft)]"
                          }`
                        }
                      >
                        {it.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </details>
            );
          })}
        </div>
        <div className="border-t border-default bg-[var(--c-surface-muted)] px-4 py-3 text-xs text-muted sm:hidden">
          <div className="font-semibold text-default">Inspector A. Patil · IO</div>
          <div className="mt-1 flex gap-3">
            <a href="/help"     className="text-brand hover:underline">{t("nav.help")}</a>
            <a href="/settings" className="text-brand hover:underline">{t("nav.settings")}</a>
          </div>
        </div>
      </aside>

      <main
        id="main-content"
        tabIndex={-1}
        role="main"
        className="mx-auto max-w-7xl px-3 py-4 sm:px-4 sm:py-6"
      >
        <Outlet />
      </main>

      <footer className="mx-auto max-w-7xl px-3 py-6 pb-20 text-center text-xs text-muted sm:px-4 sm:pb-6 no-print">
        © Maharashtra Police · DPDP Act 2023 · BNS/BNSS/BSA 2023 · For Official Use Only
      </footer>

      {/* Command palette — Ctrl/Cmd-K toggles open */}
      <CommandPalette />

      {/* Field-officer bottom nav (mobile only) */}
      <FieldBottomNav />

      <MobileIIFFAB />
    </div>
  );
}
